import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ParamsEnum } from '../models/params.model';
import {
	LINK_OFFER_TIMEOUT_MS,
	LinkOrderOffer,
	LinkOrderOfferRequest,
} from '../models/sip-order-link.model';

/**
 * Holds the uid of a SIP order link (`?sipOrder&uid=<uid>`) and loads the offer behind it.
 * Custody: the uid lives only in memory here; never stored, logged or sent in a header.
 */
@Injectable({
	providedIn: 'root',
})
export class SipOrderLinkService {
	private readonly http: HttpClient = inject(HttpClient);

	/** Memory only, see the class comment. */
	private uid: string | null = null;

	/** The captured uid, or null before capture, for a blank one, and after `clear()`. */
	get token(): string | null {
		return this.uid;
	}

	/** Reads `uid` from the query string; returns the trimmed uid, or null when missing or blank. */
	captureFromUrl(): string | null {
		const raw = new URLSearchParams(location.search).get(ParamsEnum.UID)?.trim() ?? '';
		this.uid = raw.length > 0 ? raw : null;
		return this.uid;
	}

	/** `POST .../linkorders/offer`; errors when no uid was captured, so an empty body is never sent. */
	loadOffer$(): Observable<LinkOrderOffer> {
		const token = this.uid;
		if (token === null) {
			return throwError(() => new Error('No SIP order link uid captured'));
		}
		const body: LinkOrderOfferRequest = { token };
		return this.http
			.post<LinkOrderOffer>(`${environment.baseUrl}/bookingmgmt/v2/outbox/linkorders/offer`, body)
			.pipe(timeout(LINK_OFFER_TIMEOUT_MS));
	}

	/** Drops the uid. Called when the run reaches a terminal screen. */
	clear(): void {
		this.uid = null;
	}
}
